import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {SettingsPageContainer} from '../../../../../components/Settings/SettingsPageContainer';
import {PasswordContainer} from '../../../../../components/PasswordContainer';
import {useRPC} from '../../../../../hooks/rpc';
import {WifiNetworkModel} from '../../../../../types/wifi-network';
import {ColumnModel, SettingsList} from '../../../../../components/Settings/SettingsList';
import {useMessage} from '../../../../../hooks/message';
import {useParams} from 'react-router-dom';
import iconSignal0 from '../../../../../assets/images/icons/settings/signal0.png';
import iconSignal1 from '../../../../../assets/images/icons/settings/signal1.png';
import iconSignal2 from '../../../../../assets/images/icons/settings/signal2.png';
import iconSignal3 from '../../../../../assets/images/icons/settings/signal3.png';
import wifiConnected from '../../../../../assets/images/icons/pop-up/ok.png';
import wifiNotConnected from '../../../../../assets/images/icons/pop-up/cancel.png';
import {useSelector} from 'react-redux';
import {SettingReadOnlyRowComponent} from '../../../../../components/Settings/SettingReadOnlyRowComponent';
import {useLocalization} from "../../../../../hooks/localization";

const BACK_URL = '/settings/gateway';

const keyExtractor = (item: WifiNetworkModel) => item.id

export const WiFiListScreen = memo(function WiFiListScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();

    const [wifiNetworkList, setWiFiNetworkList] = useState<WifiNetworkModel[]>([]);
    const [selectedWiFiNetwork, setSelectedWiFiNetwork] = useState<WifiNetworkModel>(null);

    const currentGatewayId = params.id;
    const selectedGateway = useSelector(state => state.control.gatewayList.find(g => g.id === currentGatewayId));
    const hasWifi = !(selectedGateway?.settings.home_network.is_ethernet && selectedGateway?.settings.remote_network.is_ethernet);

    const gatewayName = selectedGateway?.name;

    const refreshNetworkList = useCallback(() => {
        if (rpc == null) {
            // prevent exception if page is refreshed
            return;
        }

        // setWiFiNetworkList([{name:'sa', signal: 50, channel: '1', security:'1'}]);
        rpc.getWifiNetworkList(currentGatewayId)
            .then(networkList => {
                setWiFiNetworkList(networkList);
            })
            .catch(failure);
    }, [rpc, currentGatewayId, failure]);

    const onScanClick = useCallback(() => {
        setSelectedWiFiNetwork(null);
        setWiFiNetworkList([]);
        refreshNetworkList();
    }, [refreshNetworkList]);

    const onDisconnectPress = useCallback(() => {
        rpc.disconnectWifiNetwork(currentGatewayId)
            .then(() => {
                success(t('disconnected'));
            }).catch(failure);
    }, [t, failure, rpc, success, currentGatewayId]);

    const buttons = useMemo(() => [
        {
            text: t('disconnect'),
            className: 'red',
            onClick: onDisconnectPress,
            hidden: !(selectedGateway?.settings?.wifi_settings?.ssid !== '') || !hasWifi,
        },
        {
            text: t('connect'),
            url: `/settings/gateway/${currentGatewayId != null ? currentGatewayId : ''}/wifi/${selectedWiFiNetwork != null ? selectedWiFiNetwork.name : ''}`,
            className: 'blue',
            hidden: !(selectedWiFiNetwork) || !hasWifi,
        },
        {
            text: t('manual_connect'),
            url: `/settings/gateway/${currentGatewayId != null ? currentGatewayId : ''}/wifi/connect-manuel`,
            className: 'blue',
            hidden: !hasWifi,
        },
        {
            text: t('scan'),
            onClick: onScanClick,
            className: 'green',
            hidden: !hasWifi,
        },
    ], [t, currentGatewayId, selectedWiFiNetwork, onScanClick, selectedGateway, onDisconnectPress, hasWifi]);

    const displayNameExtractor = useCallback((item: WifiNetworkModel, column: ColumnModel) => {
        /*if (column.key === 'signal') {
            const icon = item.signal > 66 ? iconSignal3 :
                item.signal > 33 ? iconSignal2 :
                    item.signal > 0 ? iconSignal1 :
                        iconSignal0;
            return <img src={icon} alt=""/>;
        }*/
        if (column.key === 'signal_text') {
            return item.signal;
        }
        if (column.key === 'active'){
            if (item.active === 'yes'){
                return <span style={{color: '#57f016', textShadow: '0 0 8px #fff', fontSize: '17px'}}>CONNECTED</span>
            }
            return ''
        }

        return item[column.key] || '-';
    }, []);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 2,
            name: t('signal_power'),
            key: 'signal_text',
        },
        {
            length: 3,
            name: t('wifi_name'),
            key: 'name',
        },
        {
            length: 2,
            name: t('wifi_is_connected'),
            key: 'active',
        },
        {
            length: 2,
            name: t('channel'),
            key: 'channel',
        },
       /*{
            length: 2,
            name: t('signal_power'),
            key: 'signal',
        },*/
        {
            length: 3,
            name: t('security'),
            key: 'security',
        },
    ], [t]);

    useEffect(() => {
        if (currentGatewayId == null) {
            return;
        }

        refreshNetworkList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className={hasWifi ? undefined : 'service-edit'}
                buttons={buttons}
                backUrl={BACK_URL}
                title={gatewayName || ''}>
                {
                    renderIf(hasWifi)(() => (
                        <SettingsList
                            columns={columns}
                            items={wifiNetworkList}
                            displayNameExtractor={displayNameExtractor}
                            keyExtractor={keyExtractor}
                            selectedItem={selectedWiFiNetwork}
                            onItemSelected={setSelectedWiFiNetwork}/>
                    ), () => (
                        <div className="content-divider">
                            <div className="content-wrapper">
                                <SettingReadOnlyRowComponent
                                    title={t("wifi_is_not_being_used_on_this_device")}
                                    value=" "/>
                            </div>
                        </div>
                    ))
                }
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
